import React, { useState } from "react";
import { Send, X } from "lucide-react";
import contactService from "@/services/api/contact.service";

const ContactReplyForm = ({ contact, onReplySuccess, onCancel }) => {
  const [replyForm, setReplyForm] = useState({
    replySubject: `Re: ${contact.subject}`,
    replyMessage: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReplyForm({
      ...replyForm,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!replyForm.replyMessage.trim()) {
      setError("Reply message cannot be empty");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await contactService.replyToContact(
        contact._id,
        replyForm
      );

      if (response.success) {
        onReplySuccess(response.data);
      } else {
        setError(response.message || "Failed to send reply");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send reply");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-4 border border-gray-200 rounded-lg w-full">
      <h3 className="text-lg font-medium text-gray-900 mb-3">
        Reply to Contact
      </h3>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="replySubject"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Subject
          </label>
          <input
            type="text"
            id="replySubject"
            name="replySubject"
            value={replyForm.replySubject}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="replyMessage"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Message
          </label>
          <textarea
            id="replyMessage"
            name="replyMessage"
            rows="6"
            value={replyForm.replyMessage}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
            placeholder="Type your reply message here..."
          ></textarea>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isSubmitting}
          >
            <X size={16} className="mr-2" />
            Cancel
          </button>

          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Send Reply
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactReplyForm;
